import { defineStore } from "pinia";
import { BREAKPOINTS, IS_BP_MANAGER, IS_IBG_MANAGER } from "@/consts";
import { useAuthStore } from "@/stores/authStore";
import { useCan } from "@/stores/useCan";
import type { SVGName } from "@/SVGs";
import { computed, ref, type Ref } from "vue";
import { globali18n } from "@/i18n";
import { useRoute } from "vue-router";
import { useBreakpoints } from "@vueuse/core";

export interface INavigationLink {
  value: string;
  title: string;
  icon: SVGName;
  ibgOnly?: boolean;
  bpOnly?: boolean;
  flag?: string;
  internalWsOnly?: boolean;
  permission?: Ref<boolean>;
}

export const useNavigationStore = defineStore("navigation", () => {
  const { can } = useCan();
  const authStore = useAuthStore();
  const isMobileMenuActive = ref(false);
  const route = useRoute();
  const isDesktopCollapsed = ref(false);
  const breakpoints = useBreakpoints(BREAKPOINTS);
  const isDesktop = breakpoints.greaterOrEqual("desktop");
  const isNavExpanded = computed(
    () => isDesktop.value && !isDesktopCollapsed.value,
  );

  const allMenus: INavigationLink[] = [
    {
      value: "/",
      title: "global.dashboard",
      icon: "fas-rectangle-list",
      ibgOnly: true,
      permission: can("view", "dashboard"),
    },
    {
      value: "/new-planner",
      title: "Planner (NEW)",
      icon: "fas-clipboard-check",
      ibgOnly: true,
      permission: can("viewNew", "planner"),
    },
    {
      value: "/planner",
      title: "global.planner",
      icon: "fas-clipboard-check",
      ibgOnly: true,
      permission: can("view", "planner"),
    },
    {
      value: "/our-home/employees",
      title: "global.ourhome",
      icon: "fas-house-user",
      ibgOnly: true,
    },
    {
      value: "/employees",
      title: "global.employees",
      icon: "fas-users",
      bpOnly: true,
    },
    {
      value: "/shift-plan/schedule-plan",
      title: "global.scheduleplan",
      icon: "fas-id-card",
      ibgOnly: true,
      internalWsOnly: true,
    },
    {
      value: "/meals",
      title: "global.mealplan",
      icon: "fas-utensils",
      ibgOnly: true,
    },
    {
      value: "/activities",
      title: "global.activities",
      icon: "fas-person-running",
    },
    {
      value: "/booking/bookable-bookings",
      title: "global.booking",
      icon: "fas-key",
      ibgOnly: true,
    },
    {
      value: "/media/galleries",
      title: "global.media",
      icon: "fas-photo-video",
      ibgOnly: true,
    },
    {
      value: "/news",
      title: "global.news",
      icon: "fas-messages-lines",
    },
    {
      value: "/meeting/meetings",
      title: "global.meetings",
      icon: "fas-messages",
      ibgOnly: true,
    },
    {
      value: "/groups",
      title: "global.groups",
      icon: "fas-users",
      ibgOnly: true,
    },
  ];

  const permittedMenus = computed(() => {
    return allMenus
      .filter((link) => {
        // It should be shown if there is not any flag
        if (!link.flag) {
          return true;
        }

        // Let's check if the flag is activated
        return localStorage.getItem(link.flag);
      })
      .filter((link) => {
        if (IS_BP_MANAGER && link.ibgOnly === true) return false;
        if (IS_IBG_MANAGER && link.bpOnly === true) return false;

        // Only show work schedule if there are no external work schedules
        // set up for the currently active institution
        if (
          link.internalWsOnly &&
          authStore.currentInstitution?.settings.useExternalWorkSchedule
        ) {
          return false;
        }
        return true;
      })
      .filter((link) => {
        if (link.permission !== undefined) {
          return link.permission.value;
        }

        return true;
      })
      .map((link) => {
        return {
          ...link,
          title: globali18n.t(link.title),
        };
      });
  });

  const isAdminRoute = computed(() => {
    return route.path === "/admin" || route.path.startsWith("/admin/");
  });

  const currentMenu = computed(() => {
    if (isAdminRoute.value) {
      return;
    }

    if (route.name === "support") {
      return;
    }

    const matched = allMenus.filter((item) =>
      route.path.startsWith(item.value),
    );

    return matched.at(-1) || allMenus.at(0);
  });

  const toggleDesktopCollapsed = () => {
    isDesktopCollapsed.value = !isDesktopCollapsed.value;
  };

  return {
    allMenus,
    permittedMenus,
    isAdminRoute,
    currentMenu,
    isMobileMenuActive,
    isDesktopCollapsed,
    isNavExpanded,
    toggleDesktopCollapsed,
  };
});
